import { useEffect, useState } from "react";
import axios from "axios";
import { Globe2, RefreshCw, TrendingUp } from "lucide-react";
import { API } from "../../config";

// Country flag emoji map — used to make the dealer-country breakdown
// table readable at a glance.
const FLAGS = {
  IT: "\u{1F1EE}\u{1F1F9}", FR: "\u{1F1EB}\u{1F1F7}", ES: "\u{1F1EA}\u{1F1F8}",
  DE: "\u{1F1E9}\u{1F1EA}", UK: "\u{1F1EC}\u{1F1E7}", PT: "\u{1F1F5}\u{1F1F9}",
  BE: "\u{1F1E7}\u{1F1EA}", NL: "\u{1F1F3}\u{1F1F1}", PL: "\u{1F1F5}\u{1F1F1}",
  SE: "\u{1F1F8}\u{1F1EA}", NO: "\u{1F1F3}\u{1F1F4}", FI: "\u{1F1EB}\u{1F1EE}",
  DK: "\u{1F1E9}\u{1F1F0}", CZ: "\u{1F1E8}\u{1F1FF}", GR: "\u{1F1EC}\u{1F1F7}",
  CH: "\u{1F1E8}\u{1F1ED}", GBZ: "\u{1F1EC}\u{1F1EE}", IRL: "\u{1F1EE}\u{1F1EA}",
};
const flag = (c) => FLAGS[c] || "\u{1F310}";

// Color a percentage based on heuristic "ideal" range. The dropdown
// default is the dealer's country, so a perfect auto-detection gives
// ~0% override rate; >25% means the heuristic is wrong for that market.
const rateColor = (pct) => {
  if (pct >= 25) return "text-red-400";
  if (pct >= 10) return "text-lojack-clause-400";
  return "text-lojack-blue-400";
};

export const SocChangesWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = async (windowDays = days) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/analytics/soc-changes`, {
        params: { days: windowDays },
      });
      setData(res.data);
    } catch (err) {
      console.error("SOC analytics fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  return (
    <div
      className="bg-[#0F0F0F] border border-gray-800 rounded-2xl p-6"
      data-testid="soc-changes-widget"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-lojack-red/15 border border-lojack-red/30 flex items-center justify-center">
            <Globe2 className="w-5 h-5 text-lojack-red" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">
              Cambi Centrale Operativa (pre-firma)
            </h3>
            <p className="text-gray-500 text-xs">
              Quanti clienti modificano la SOC default prima di firmare il contratto
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-[#1A1A1A] text-gray-300 text-xs border border-gray-700 rounded-lg px-2 py-1 cursor-pointer focus:border-lojack-red outline-none"
            data-testid="soc-changes-window"
          >
            <option value={7}>Ultimi 7 g</option>
            <option value={30}>Ultimi 30 g</option>
            <option value={90}>Ultimi 90 g</option>
            <option value={365}>Ultimo anno</option>
          </select>
          <button
            type="button"
            onClick={() => fetchData(days)}
            className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
            data-testid="soc-changes-refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="py-10 text-center text-gray-500 text-sm">Caricamento metriche…</div>
      )}

      {data && (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="bg-[#141414] border border-gray-800 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Nuovi clienti</p>
              <p className="text-2xl font-bold text-white mt-1" data-testid="soc-kpi-new-customers">
                {data.new_customers}
              </p>
            </div>
            <div className="bg-[#141414] border border-gray-800 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Cambi pre-firma</p>
              <p className="text-2xl font-bold text-white mt-1" data-testid="soc-kpi-changes-before">
                {data.changes_before_signature}
              </p>
            </div>
            <div className="bg-[#141414] border border-gray-800 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Clienti che hanno cambiato</p>
              <p className="text-2xl font-bold text-white mt-1" data-testid="soc-kpi-unique-changers">
                {data.unique_customers_changed_before}
              </p>
            </div>
            <div className="bg-[#141414] border border-gray-800 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Tasso override</p>
              <p
                className={`text-2xl font-bold mt-1 ${rateColor(data.change_rate_pct)}`}
                data-testid="soc-kpi-change-rate"
              >
                {data.change_rate_pct}%
              </p>
            </div>
          </div>

          {/* Top transitions */}
          {data.top_transitions?.length > 0 ? (
            <div className="mb-5">
              <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5" />
                Top transizioni from → to
              </p>
              <div className="flex flex-wrap gap-2" data-testid="soc-top-transitions">
                {data.top_transitions.map((t, i) => (
                  <div
                    key={`${t.from}-${t.to}-${i}`}
                    className="inline-flex items-center gap-2 bg-[#141414] border border-gray-800 rounded-lg px-3 py-1.5"
                  >
                    <span className="text-base">{flag(t.from)}</span>
                    <span className="text-xs text-gray-500">{t.from}</span>
                    <span className="text-gray-600 text-xs">→</span>
                    <span className="text-base">{flag(t.to)}</span>
                    <span className="text-xs text-gray-300 font-medium">{t.to}</span>
                    <span className="ml-2 text-xs bg-lojack-red/15 text-lojack-red border border-lojack-red/30 rounded-full px-2 py-0.5 font-mono">
                      {t.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-sm italic mb-5" data-testid="soc-no-transitions">
              Nessun cambio SOC pre-firma nel periodo selezionato — l'auto-rilevamento sta funzionando.
            </p>
          )}

          {/* Breakdown by dealer country */}
          {data.by_dealer_country?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-2">
                Override per paese dealer
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="soc-by-dealer-country">
                  <thead>
                    <tr className="text-gray-500 text-xs">
                      <th className="text-left py-2 px-2 font-medium">Paese dealer</th>
                      <th className="text-right py-2 px-2 font-medium">Clienti</th>
                      <th className="text-right py-2 px-2 font-medium">Cambi</th>
                      <th className="text-right py-2 px-2 font-medium">Tasso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_dealer_country.map((row) => (
                      <tr key={row.dealer_country} className="border-t border-gray-800/60">
                        <td className="py-2 px-2 text-gray-200">
                          <span className="mr-2">{flag(row.dealer_country)}</span>
                          {row.dealer_country}
                        </td>
                        <td className="py-2 px-2 text-right text-gray-300 font-mono">{row.customers}</td>
                        <td className="py-2 px-2 text-right text-gray-300 font-mono">{row.changes}</td>
                        <td className={`py-2 px-2 text-right font-mono font-semibold ${rateColor(row.rate_pct)}`}>
                          {row.rate_pct}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
